import React from 'react';
import { Form, Input, Button, Select, Spin, Card, Result, message, Table, Alert, Modal, Tag, Progress } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { Link } from 'react-router-dom';
import * as serverconfig from '../serverconn';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  getOnboardingUploadTypes,
  getOperationalUploadTypes,
  getTemplateConfig,
} from '../dataUploadTemplates';
import { downloadUploadTemplate, fetchUploadTemplateSample, formatDataSize, parseUploadFile } from '../dataUploadUtils';
import {
  downloadImportJobReport,
  formatImportJobSummary,
  importStatusTagColor,
  pollImportJobUntilDone,
} from '../importJobUtils';

var CryptoJS = require('crypto-js');

const FormItem = Form.Item;
const { Option } = Select;
const dateFormat = 'DD/MM/YYYY';
const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;

var token = '';
var sacco = '';
var username = '';

class DataUploadForm extends React.Component {
  state = {
    date: moment().format(dateFormat).toString(),
    excelfilefile: null,
    saccoboject: {},
    datalist: [],
    uploadtype: this.props.fixedUploadType || '',
    columns: [],
    message: '',
    companyprofile: {},
    datarequested: false,
    datasubmittedsuccessfully: false,
    parseStats: null,
    headerMismatches: [],
    serverValidation: null,
    validating: false,
    receiving_email: '',
    importJob: null,
    pollingImport: false,
    confirmModalOpen: false,
    confirmContext: null,
    templateSampleRow: null,
    downloadingTemplate: false,
  };

  componentDidMount() {
    if (localStorage.getItem('sacco')) {
      sacco = CryptoJS.AES.decrypt(localStorage.getItem('sacco'), 'my-secret-key@12345').toString(CryptoJS.enc.Utf8);
      username = CryptoJS.AES.decrypt(localStorage.getItem('username'), 'my-secret-key@123').toString(CryptoJS.enc.Utf8);
    } else {
      sacco = '';
      username = '';
    }

    token = localStorage.getItem('token') || '';

    axios.defaults.headers = {
      'Content-Type': 'application/json',
      Authorization: `Token ${token}`,
    };

    axios.get(`${serverconfig.backendserverurl}/api/saccos/${sacco}`).then((res) => {
      this.setState({
        saccoboject: res.data,
        companyprofile: res.data,
        datarequested: false,
        receiving_email: res.data.company_email || this.state.receiving_email,
      });
    });

    if (this.state.uploadtype) {
      this.prefetchTemplateSample(this.state.uploadtype);
    }
  }

  componentWillUnmount() {
    this._pollCancelled = true;
  }

  handleUploadTypeChange = (val) => {
    this.setState({
      uploadtype: val,
      datalist: [],
      columns: [],
      excelfilefile: null,
      parseStats: null,
      headerMismatches: [],
      templateSampleRow: null,
    });
    this.prefetchTemplateSample(val);
  };

  prefetchTemplateSample = async (uploadType) => {
    if (!uploadType || !sacco) {
      return;
    }
    try {
      const sampleRow = await fetchUploadTemplateSample(
        uploadType,
        sacco,
        serverconfig.backendserverurl,
      );
      if (sampleRow) {
        this.setState({ templateSampleRow: sampleRow });
      }
    } catch (err) {
      // Keep static fallback from dataUploadTemplates.js
    }
  };

  handleDownloadTemplate = async () => {
    if (!this.state.uploadtype) {
      message.warning('Select an upload type first.');
      return;
    }
    this.setState({ downloadingTemplate: true });
    try {
      const result = await downloadUploadTemplate(
        this.state.uploadtype,
        sacco,
        serverconfig.backendserverurl,
      );
      if (result.ok) {
        this.setState({ templateSampleRow: result.sampleRow || this.state.templateSampleRow });
        message.success('Template downloaded using your institution\'s current setup data.');
      } else {
        message.error('Could not generate template for this upload type.');
      }
    } catch (err) {
      message.error('Could not download template.');
    } finally {
      this.setState({ downloadingTemplate: false });
    }
  };

  handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      return;
    }

    if (!this.state.uploadtype) {
      message.warning('Select an upload type before uploading a file.');
      e.target.value = '';
      return;
    }

    this.setState({
      datarequested: true,
      excelfilefile: selectedFile,
      datalist: [],
      columns: [],
      parseStats: null,
      headerMismatches: [],
    });

    const runParse = (sampleRow) => {
      parseUploadFile(selectedFile, this.state.uploadtype, sampleRow)
        .then((result) => {
          if (result.headerMismatches && result.headerMismatches.length) {
            message.error(result.error || 'Template column mismatch.');
            this.setState({
              datarequested: false,
              headerMismatches: result.headerMismatches,
            });
            return;
          }

          const { stats } = result;
          if (result.datalist.length === 0) {
            message.warning('No valid rows to import. Review the validation summary below.');
          } else if (result.ok) {
            message.success(`Ready to import ${result.datalist.length} row(s).`);
          } else {
            message.warning(result.error || 'Fix validation issues before uploading.');
          }

          if (stats.skippedSampleRows > 0) {
            message.info(`Skipped ${stats.skippedSampleRows} sample row(s) from the template.`);
          }

          this.setState({
            datalist: result.datalist || [],
            columns: result.columns || [],
            parseStats: stats,
            serverValidation: null,
            datarequested: false,
            templateSampleRow: sampleRow || this.state.templateSampleRow,
          });
        })
        .catch((err) => {
          message.error(err.message || 'Failed to process the uploaded file.');
          this.setState({ datarequested: false });
        });
    };

    if (this.state.templateSampleRow) {
      runParse(this.state.templateSampleRow);
      return;
    }

    fetchUploadTemplateSample(this.state.uploadtype, sacco, serverconfig.backendserverurl)
      .then((sampleRow) => runParse(sampleRow))
      .catch(() => runParse(null));
  };

  startImportJobPolling = (job) => {
    if (!job || !job.is_background) {
      return;
    }
    this._pollCancelled = false;
    this.setState({ pollingImport: true, importJob: job });
    pollImportJobUntilDone(job.id, {
      onUpdate: (updated) => {
        if (!this._pollCancelled) {
          this.setState({ importJob: updated });
        }
      },
    })
      .then((finalJob) => {
        if (this._pollCancelled) {
          return;
        }
        this.setState({
          pollingImport: false,
          importJob: finalJob,
          message: finalJob.result_message || this.state.message,
          datasubmittedsuccessfully: true,
        });
        if (finalJob.status === 'done') {
          if (finalJob.failed_count > 0) {
            message.warning(
              `Import job #${finalJob.id} finished with ${finalJob.failed_count} failed row(s).` +
                (finalJob.email_sent ? ' A report was emailed to you.' : '')
            );
          } else {
            message.success(
              `Import job #${finalJob.id} completed.` +
                (finalJob.email_sent ? ' Report emailed.' : '')
            );
          }
        } else {
          message.error(
            `Import job #${finalJob.id} failed.` +
              (finalJob.email_sent ? ' Error details were emailed to you.' : ' Check the import report below.')
          );
        }
        if (this.props.onUploadSuccess) {
          this.props.onUploadSuccess(finalJob.result_message);
        }
      })
      .catch(() => {
        if (!this._pollCancelled) {
          message.warning('Could not refresh import status. Check import job report later.');
          this.setState({ pollingImport: false, datasubmittedsuccessfully: true });
        }
      });
  };

  submitUpload = () => {
    this.setState({ datarequested: true });

    const form_data = new FormData();
    form_data.append('sacco', sacco);
    form_data.append('datalist', JSON.stringify(this.state.datalist));
    form_data.append('username', username);
    form_data.append('uploadtype', this.state.uploadtype);
    const reportEmail =
      (this.state.receiving_email || '').trim() ||
      (this.state.companyprofile && this.state.companyprofile.company_email) ||
      '';
    if (reportEmail) {
      form_data.append('receiving_email', reportEmail);
    }

    axios
      .post(`${serverconfig.backendserverurl}/customqueries/final_dataupload`, form_data, {
        headers: { 'content-type': 'multipart/form-data' },
      })
      .then((res) => {
        const msg = res.data.message;
        const job = res.data.import_job || null;
        const isBackground = job && job.is_background;

        if (this.props.onUploadSuccess && !isBackground) {
          this.props.onUploadSuccess(msg);
          this.setState({
            message: msg,
            datarequested: false,
            datasubmittedsuccessfully: false,
            datalist: [],
            columns: [],
            excelfilefile: null,
            parseStats: null,
            importJob: job,
          });
          return;
        }

        if (isBackground) {
          message.info(msg);
          this.setState({
            message: msg,
            datarequested: false,
            importJob: job,
            datalist: [],
            columns: [],
            excelfilefile: null,
            parseStats: null,
          });
          this.startImportJobPolling(job);
          return;
        }

        this.setState({
          message: msg,
          datarequested: false,
          datasubmittedsuccessfully: true,
          importJob: job,
          datalist: [],
          columns: [],
        });
        if (this.props.onUploadSuccess) {
          this.props.onUploadSuccess(msg);
        }
      })
      .catch((err) => {
        const errMsg = err.response?.data?.message || 'Upload failed. Please try again.';
        message.error(errMsg);
        this.setState({ datarequested: false });
      });
  };

  runServerValidation = () => {
    const form_data = new FormData();
    form_data.append('sacco', sacco);
    form_data.append('datalist', JSON.stringify(this.state.datalist));
    form_data.append('uploadtype', this.state.uploadtype);

    return axios.post(
      `${serverconfig.backendserverurl}/customqueries/validate_data_upload`,
      form_data,
      { headers: { 'content-type': 'multipart/form-data' } }
    );
  };

  showConfirmModal = (serverValidation, templateConfig, dataSizeBytes, isBackground) => {
    this.setState({
      confirmModalOpen: true,
      confirmContext: { serverValidation, templateConfig, dataSizeBytes, isBackground },
    });
  };

  handleConfirmUpload = () => {
    this.setState({ confirmModalOpen: false, confirmContext: null }, () => {
      this.submitUpload();
    });
  };

  handleCancelConfirm = () => {
    this.setState({ confirmModalOpen: false, confirmContext: null });
  };

  renderConfirmModal() {
    const { confirmModalOpen, confirmContext, receiving_email, companyprofile, datalist } = this.state;
    if (!confirmModalOpen || !confirmContext) {
      return null;
    }

    const { serverValidation, templateConfig, dataSizeBytes, isBackground } = confirmContext;
    const warnings = (serverValidation && serverValidation.warnings) || [];
    const info = (serverValidation && serverValidation.info) || [];

    return (
      <Modal
        visible={confirmModalOpen}
        title="Confirm data import"
        width={560}
        okText="Upload"
        cancelText="Cancel"
        onOk={this.handleConfirmUpload}
        onCancel={this.handleCancelConfirm}
      >
        <p>Upload type: <strong>{templateConfig?.label}</strong></p>
        <p>Rows to import: <strong>{datalist.length}</strong></p>
        <p>Payload size: <strong>{formatDataSize(dataSizeBytes)}</strong></p>
        {isBackground && (
          <p>This import runs in the background. Reconcile loan trackers after bulk loan imports finish.</p>
        )}
        {info.map((item) => (
          <p key={`info-${item.message}`} style={{ color: '#1890ff' }}>{item.message}</p>
        ))}
        {warnings.map((item) => (
          <p key={`warn-${item.row}-${item.message}`} style={{ color: '#fa8c16' }}>
            {item.row ? `Row ${item.row}: ` : ''}{item.message}
          </p>
        ))}
        <FormItem label="Email for import errors & report (recommended for background imports)" style={{ marginTop: 12, marginBottom: 0 }}>
          <Input
            value={receiving_email}
            placeholder={companyprofile.company_email || 'name@example.com'}
            onChange={(e) => this.setState({ receiving_email: e.target.value })}
          />
        </FormItem>
      </Modal>
    );
  }

  handleFormSubmit = () => {
    if (this.state.datalist.length <= 0) {
      message.error('No valid rows to upload.');
      return;
    }

    if (this.state.parseStats && this.state.parseStats.businessErrors && this.state.parseStats.businessErrors.length) {
      message.error('Fix client-side validation errors shown below.');
      return;
    }

    const dataSizeBytes = JSON.stringify(this.state.datalist).length;
    const templateConfig = getTemplateConfig(this.state.uploadtype);
    const isBackground = templateConfig && !['chartofaccounts', 'coa_opening_balances', 'loanopeningbalances', 'loanschedules', 'special_journal_entries'].includes(this.state.uploadtype);

    this.setState({ validating: true });
    this.runServerValidation()
      .then((res) => {
        this.setState({ validating: false, serverValidation: res.data });
        this.showConfirmModal(res.data, templateConfig, dataSizeBytes, isBackground);
      })
      .catch((err) => {
        this.setState({ validating: false });
        const validation = err.response && err.response.data;
        if (validation && validation.errors) {
          this.setState({ serverValidation: validation });
          message.error(validation.message || 'Server validation failed. Fix errors below.');
        } else {
          message.error((validation && validation.message) || 'Validation request failed.');
        }
      });
  };

  handleReconcileLoans = () => {
    const form_data = new FormData();
    form_data.append('sacco', sacco);
    this.setState({ validating: true });
    axios
      .post(`${serverconfig.backendserverurl}/customqueries/reconcile_loan_trackers`, form_data, {
        headers: { 'content-type': 'multipart/form-data' },
      })
      .then((res) => {
        message.success(res.data.message);
        this.setState({ validating: false });
      })
      .catch((err) => {
        message.error((err.response && err.response.data && err.response.data.message) || 'Reconciliation failed.');
        this.setState({ validating: false });
      });
  };

  renderValidationList(items, limit = 8) {
    if (!items || !items.length) {
      return null;
    }
    return (
      <ul style={{ marginTop: 8, marginBottom: 0 }}>
        {items.slice(0, limit).map((item, idx) => (
          <li key={`v-${idx}-${item.row}-${item.message}`}>
            {item.row ? `Row ${item.row}: ` : ''}{item.message}
          </li>
        ))}
        {items.length > limit && <li>...and {items.length - limit} more</li>}
      </ul>
    );
  }

  renderParseSummary() {
    const { parseStats, serverValidation } = this.state;
    if (!parseStats && !serverValidation) {
      return null;
    }

    const businessErrors = (parseStats && parseStats.businessErrors) || [];
    const businessWarnings = (parseStats && parseStats.businessWarnings) || [];
    const serverErrors = (serverValidation && serverValidation.errors) || [];

    const hasErrors = businessErrors.length > 0 || serverErrors.length > 0;

    return (
      <div style={{ marginBottom: 16 }}>
        {parseStats && (
          <Alert
            type={hasErrors ? 'error' : parseStats.importedRows > 0 ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: 12 }}
            message="Import preview summary"
            description={(
              <div>
                <div>Total rows in file: {parseStats.totalRows}</div>
                <div>Rows ready to import: {parseStats.importedRows}</div>
                {parseStats.skippedSampleRows > 0 && <div>Sample rows skipped: {parseStats.skippedSampleRows}</div>}
                {parseStats.skippedInvalidRows > 0 && (
                  <div>
                    Invalid rows skipped: {parseStats.skippedInvalidRows}
                    {this.renderValidationList(
                      parseStats.invalidRows.map((item) => ({
                        row: item.rowNumber,
                        message: item.issues.join('; '),
                      }))
                    )}
                  </div>
                )}
                {parseStats.businessSummary && parseStats.businessSummary.path_a > 0 && (
                  <div>Path A (balance snapshot): {parseStats.businessSummary.path_a} loan(s)</div>
                )}
                {parseStats.businessSummary && parseStats.businessSummary.path_b > 0 && (
                  <div>Path B (full issuance): {parseStats.businessSummary.path_b} loan(s)</div>
                )}
              </div>
            )}
          />
        )}
        {businessErrors.length > 0 && (
          <Alert type="error" showIcon message="Fix before upload" description={this.renderValidationList(businessErrors)} style={{ marginBottom: 12 }} />
        )}
        {businessWarnings.length > 0 && (
          <Alert type="warning" showIcon message="Warnings" description={this.renderValidationList(businessWarnings)} style={{ marginBottom: 12 }} />
        )}
        {(parseStats && parseStats.businessInfo && parseStats.businessInfo.length > 0) && (
          <Alert type="info" showIcon message="Guidance" description={this.renderValidationList(parseStats.businessInfo)} style={{ marginBottom: 12 }} />
        )}
        {serverValidation && serverErrors.length > 0 && (
          <Alert type="error" showIcon message="Server validation" description={this.renderValidationList(serverErrors)} />
        )}
      </div>
    );
  }

  renderUploadTypeOptions() {
    const onboardingDone = this.state.companyprofile.client_onboarding_done === true;
    const options = onboardingDone ? [] : getOnboardingUploadTypes();
    options.push(...getOperationalUploadTypes());

    return options.map((item) => (
      <Option key={item.value} value={item.value}>
        {item.label}
      </Option>
    ));
  }

  renderImportJobPanel() {
    const { importJob, pollingImport } = this.state;
    if (!importJob) {
      return null;
    }
    const percent = importJob.total_rows
      ? Math.round(
          ((importJob.imported_count + importJob.skipped_count + importJob.failed_count) /
            Math.max(importJob.total_rows, 1)) *
            100
        )
      : importJob.status === 'done'
        ? 100
        : importJob.status === 'running'
          ? 50
          : 10;

    return (
      <Card title={`Import job #${importJob.id}`} style={{ marginBottom: 16 }}>
        <p>
          <Tag color={importStatusTagColor(importJob.status)}>{importJob.status.toUpperCase()}</Tag>
          &nbsp;{importJob.uploadtype}
        </p>
        {(pollingImport || importJob.status === 'running' || importJob.status === 'queued') && (
          <Progress percent={percent} status="active" style={{ marginBottom: 12 }} />
        )}
        <p>{importJob.result_message || this.state.message}</p>
        <p>{formatImportJobSummary(importJob)}</p>
        {importJob.email_sent && (
          <p style={{ color: '#1890ff' }}>Import report emailed to the address on file.</p>
        )}
        {(importJob.status === 'done' || importJob.status === 'failed') && (
          <Button type="primary" icon={<DownloadOutlined />} onClick={() => downloadImportJobReport(importJob.id)}>
            Download import report (Excel)
          </Button>
        )}
      </Card>
    );
  }

  render() {
    if (this.state.datarequested === true || this.state.validating === true) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin indicator={antIcon} />
        </div>
      );
    }

    if (this.state.pollingImport || this.state.datasubmittedsuccessfully === true) {
      const job = this.state.importJob;
      const failed = job && job.status === 'failed';
      return (
        <Card>
          {this.renderImportJobPanel()}
          <Result
            status={failed ? 'error' : this.state.pollingImport ? 'info' : 'success'}
            title={this.state.pollingImport ? 'Import in progress' : failed ? 'Import finished with errors' : 'Import complete'}
            subTitle={this.state.message}
            extra={[
              job && (job.status === 'done' || job.status === 'failed') ? (
                <Button key="download" icon={<DownloadOutlined />} onClick={() => downloadImportJobReport(job.id)}>
                  Download report
                </Button>
              ) : null,
              <Button type="primary" key="finish" onClick={() => window.location.reload(false)}>
                Finish
              </Button>,
            ]}
          />
        </Card>
      );
    }

    const templateConfig = getTemplateConfig(this.state.uploadtype);
    const { hideTypeSelector, compact } = this.props;

    return (
      <div>
        {this.renderConfirmModal()}
        {!compact && <h3>Data upload section</h3>}
        <Alert
          type="warning"
          showIcon
          message="Download the template first. Sample row 2 uses your institution's accounts, types, and districts — delete it or leave it (auto-skipped). Do not change column headings."
          style={{ marginBottom: 16 }}
        />
        {!compact && (
          <Alert
            type="info"
            showIcon
            message={(
              <span>
                Need member-specific JSON imports (journals, statements, loan workflow)?
                {' '}
                <Link to="/dataonboarding_json/">Use the JSON import tool</Link>
                {' '}
                | Loan opening balances also available on{' '}
                <Link to="/loanopening_balances_upload/">dedicated page</Link>
              </span>
            )}
            style={{ marginBottom: 16 }}
          />
        )}
        <Card>
          <Form layout="vertical">
            {!hideTypeSelector && (
              <FormItem label="Upload type">
                <Select
                  value={this.state.uploadtype || undefined}
                  onChange={this.handleUploadTypeChange}
                  placeholder="Upload type"
                  style={{ width: '100%' }}
                >
                  {this.renderUploadTypeOptions()}
                </Select>
              </FormItem>
            )}

            {templateConfig && (
              <Alert type="info" showIcon message={templateConfig.hint} style={{ marginBottom: 16 }} />
            )}

            {this.state.headerMismatches.length > 0 && (
              <Alert
                type="error"
                showIcon
                message="Column heading mismatch"
                description={(
                  <ul style={{ marginBottom: 0 }}>
                    {this.state.headerMismatches.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
                style={{ marginBottom: 16 }}
              />
            )}

            <FormItem label="Template">
              <Button
                icon={<DownloadOutlined />}
                disabled={!this.state.uploadtype}
                loading={this.state.downloadingTemplate}
                onClick={this.handleDownloadTemplate}
              >
                Download Excel template
              </Button>
              {templateConfig && (
                <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  Columns: {templateConfig.headers.join(', ')}
                </div>
              )}
            </FormItem>

            <FormItem label="Excel data file">
              <Input type="file" accept=".xlsx,.xls" onChange={this.handleFileChange} />
            </FormItem>
          </Form>
        </Card>

        <br />
        {this.renderImportJobPanel()}
        {this.renderParseSummary()}
        {this.state.datalist.length > 0 && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0 }}>Processed Data ({this.state.datalist.length} rows)</h2>
              &nbsp;&nbsp;
              <Button type="primary" onClick={this.handleFormSubmit} loading={this.state.validating}>
                Upload data
              </Button>
              {(this.props.showReconcileLoans || ['loans', 'loanschedules', 'loanrepayments'].includes(this.state.uploadtype)) && (
                <Button style={{ marginLeft: 8 }} onClick={this.handleReconcileLoans} loading={this.state.validating}>
                  Reconcile loan trackers
                </Button>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <Table
                dataSource={this.state.datalist}
                columns={this.state.columns}
                scroll={{ x: 1000 }}
                bordered
                pagination={{ showQuickJumper: true, showSizeChanger: true }}
                size="small"
                rowKey={(record, index) => String(index)}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default DataUploadForm;
